import axios, { AxiosError } from 'axios';

import IBimVisualizerApiProvider from './IBimVisualizerApiProvider';
import { UUID } from '../../utils/typeDefinitions';
import Product from '../../model/Product';
import Apartment from '../../model/Apartment';
import { GbContextParams, ProjectContextParams, ServiceUrlParams } from './queryParams';
import MaterialFactory from '../../model/material/MaterialFactory'
import { ConfigurationState } from '../../store/configurationState/interfaces';
import { IModelResponse } from './IModelResponse';
import Document from '../../model/document/Document';
import Material from '../../model/material/Material';
import SceneObjectData from '../../model/sceneObjectData/SceneObjectData';
import IGbContext from '../../interfaces/IGbContext';
import handleApiError from './errorHandler';
import User from '../../model/User';
import createSceneObjectData from '../../model/sceneObjectData/SceneObjectDataFactory';
import { getCurrentUserSchema } from './schema/getCurrentUserSchema';

export default class BimVisualizerApiProvider implements IBimVisualizerApiProvider {
    configuration : ConfigurationState;
    token? : string;
    materialFactory : MaterialFactory;

    constructor(configuration: ConfigurationState, token?: string)
    {
        this.configuration = configuration;
        this.token = token;
        this.materialFactory = new MaterialFactory(configuration.visualizerApiUrl);
    }

    getServiceUrlParams() : ServiceUrlParams
    {
        return {
            bimVaultApiUrl: this.configuration.bimVaultApiUrl,
            coreApiUrl: this.configuration.coreApiUrl,
            threeDeeApiUrl: this.configuration.threeDeeApiUrl
        };
    }

    getApartmentModelByGlobalIds = async (projectGlobalId: UUID, apartmentGlobalId: UUID) : Promise<IModelResponse | null> => {
            
        try {
            const requestParams = this.getServiceUrlParams();
            const request = axios.get(`${this.configuration.visualizerApiUrl.href}api/v1/public/bim/projects/${projectGlobalId}/apartments/${apartmentGlobalId}`, {
                headers: {
                    'Content-Type': 'application/json',
                    // Add any authorization tokens or other headers if needed
                },
                params: requestParams
            });
            const response = await request;
            console.log(response.data);

            const products: Product[] = response.data["products"].map((p: any) => Product.fromJSON(p, null));
            const apartments: Apartment[] = response.data["apartments"].map((a: any) => Apartment.fromJSON(a));
            const materials: Material[] = response.data["materials"].map((m: any) => this.materialFactory.materialFromJson(m));

            return { products, apartments, materials };
            
        } catch (error) {
            console.error('Error fetching data:', error);
        }

        return null; 
    };

    getApartmentModelByGbContext = async (gbContext: IGbContext ) : Promise<IModelResponse | null> => {

        if (!this.token) {
            console.error('Token is required for GbContext API calls');
            return null;
        }

        const queryParams = {...gbContext, ...this.getServiceUrlParams()};
            
        try {
            const response = await axios.get(`${this.configuration.visualizerApiUrl.href}api/v2/private/model/apartment`, {
                headers: {
                    'Content-Type': 'application/json',
                    // Add any authorization tokens or other headers if needed
                    Authorization: `Bearer ${this.token}`,
                },
                params: queryParams,
                
            });
            console.log(response.data);
            
            const products: Product[] = response.data["products"].map((p: any) => Product.fromJSON(p, null));
            const apartments: Apartment[] = response.data["apartments"].map((a: any) => Apartment.fromJSON(a));
            const materials: Material[] = response.data["materials"].map((m: any) => this.materialFactory.materialFromJson(m));

            return { products, apartments, materials };

        } catch (error) {
            handleApiError(error);
        }

        return null; 
    };

    getSpatialStructureModel = async (projectGlobalId: UUID, spatialStructureGlobalId: UUID) : Promise<IModelResponse | null> => {
            
        try {
            const response = await axios.get(`${this.configuration.visualizerApiUrl.href}api/v1/public/bim/projects/${projectGlobalId}/spatial-structures/${spatialStructureGlobalId}`, {
                headers: {
                    'Content-Type': 'application/json',
                    // Add any authorization tokens or other headers if needed
                },
                params: this.getServiceUrlParams()
            });
            console.log(response.data);

            const products: Product[] = response.data["products"].map((p: any) => Product.fromJSON(p, null));
            const apartments: Apartment[] = response.data["apartments"].map((a: any) => Apartment.fromJSON(a));
            const materials: Material[] = response.data["materials"].map((m: any) => this.materialFactory.materialFromJson(m));

            return { products, apartments, materials };
            
        } catch (error) {
            console.error('Error fetching data:', error);
        }

        return null; 
    };

    getProjectDocuments = async (projectUuid: UUID, entityUuid: UUID) : Promise<Document[]> => {
        return this.getDocuments({ projectUuid: projectUuid, entityUuid: entityUuid });
    }
    
    getCoreDocuments = async (gbContext: IGbContext) : Promise<Document[]> => {
        return this.getDocuments(gbContext);
    }

    getChecklistCoreDocument = async (gbContext: IGbContext): Promise<Document> => {
        try {
            const response = await axios.get(`${this.configuration.visualizerApiUrl.href}api/v1/private/checklist-document`, {
                headers: {
                    'Content-Type': 'application/json',
                    ...(this.token ? { Authorization: `Bearer ${this.token}` } : {})
                },
                params: { ...gbContext, ...this.getServiceUrlParams() }
            });
            console.log(response.data);
            return Document.fromJson(response.data);
        } catch (error) {
            if (!handleApiError(error)) {
                console.error('Error fetching checklist document:', error);
            }
            throw error;
        }
    }

    getDocuments = async (queryParams: IGbContext | ProjectContextParams) : Promise<Document[]> => {
            
        try {
            const response = await axios.get(`${this.configuration.visualizerApiUrl.href}api/v1/private/documents`, {
                headers: {
                    'Content-Type': 'application/json',
                    ...(this.token ? { Authorization: `Bearer ${this.token}` } : {})
                },
                params: {   ...queryParams, 
                            ...this.getServiceUrlParams() }
            });

            console.log(response.data);

            const docCandidates = response.data.documents ?? [];

            const documents = docCandidates.map((doc: any) => Document.fromJson(doc));
            return documents;

        } catch (error) {

            if (!handleApiError(error)) {
                console.error('Error fetching documents:', error);
            }
        }

        return [];
    };

    getDocument = async (documentUuid: UUID, queryParams: IGbContext | undefined) : Promise<Document | null> => {
        try {
            const response = await axios.get(`${this.configuration.visualizerApiUrl.href}api/v1/private/documents/${documentUuid}`, {
                headers: {
                    'Content-Type': 'application/json',
                    ...(this.token ? { Authorization: `Bearer ${this.token}` } : {})
                },
                params: {   ...queryParams, 
                            ...this.getServiceUrlParams() }
            });
            console.log(response.data);
            return Document.fromJson(response.data);
        } catch (error) {
            if (!handleApiError(error)) {
                console.error('Error fetching documents:', error);
            }
        }

        return null;
    };

    addProjectDocument = async (projectUuid: UUID, entityUuid: UUID, documentName: string): Promise<UUID | null> => {
        return this.addDocument({ projectUuid: projectUuid, entityUuid: entityUuid }, documentName);
    }

    addCoreDocument = async (documentName: string, coreContext: IGbContext): Promise<UUID | null> => {
        return this.addDocument(coreContext, documentName);
    }

    addDocument = async (queryParams: IGbContext | ProjectContextParams, documentName: string) : Promise<UUID | null> => {
        try {
            
            const response = await axios.post(
                            `${this.configuration.visualizerApiUrl.href}api/v1/private/documents`, 
                            {name: documentName}, {
                headers: {
                    'Content-Type': 'application/json',
                    ...(this.token ? { Authorization: `Bearer ${this.token}` } : {})
                },
                params: {   ...queryParams,
                            ...this.getServiceUrlParams() }

            });
            console.log(response.data);

            return response.data.documentUuid ?? null;
            
        } catch (error) {
            if (!handleApiError(error)) {
                console.error('Error fetching documents:', error);
            }
        }

        return null;
    };

    addSceneObject = async (documentUuid: UUID, sceneObjectData: SceneObjectData): Promise<SceneObjectData | null> => {
        try {

            const payload = {   uuid: sceneObjectData.uuid, 
                                type: sceneObjectData.type, 
                                data: JSON.stringify(sceneObjectData.toJson())};

            const response = await axios.post(
                            `${this.configuration.visualizerApiUrl.href}api/v1/private/scene-objects`, 
                            payload, {
                headers: {
                    'Content-Type': 'application/json',
                    ...(this.token ? { Authorization: `Bearer ${this.token}` } : {})
                },
                params: {   documentUuid: documentUuid,
                            ...this.getServiceUrlParams() }
            });
            console.log(response.data);

            return createSceneObjectData(response.data);
            
        } catch (error) {
            console.error('Error adding scenee object:', error);
        }

        return null;
    };

    updateSceneObject = async (sceneObjectUuid: UUID, sceneObjectData: SceneObjectData): Promise<SceneObjectData | null> => {
        try {
            const payload = {   type: sceneObjectData.type,
                                data: JSON.stringify(sceneObjectData.toJson()) };

            const response = await axios.put(
                            `${this.configuration.visualizerApiUrl.href}api/v1/private/scene-objects/${sceneObjectUuid}`,
                            payload, {
                headers: {
                    'Content-Type': 'application/json',
                    ...(this.token ? { Authorization: `Bearer ${this.token}` } : {})
                },
                params: this.getServiceUrlParams()
            });

            return createSceneObjectData(response.data);
        } catch (error) {
            console.error('Error updating scene object:', error);
        }

        return null;
    };

    removeSceneObject = async (sceneObjectUuid: UUID): Promise<boolean> => {
        try {

            const response = await axios.delete(
                            `${this.configuration.visualizerApiUrl.href}api/v1/private/scene-objects/${sceneObjectUuid}`, {
                headers: {
                    'Content-Type': 'application/json',
                    ...(this.token ? { Authorization: `Bearer ${this.token}` } : {})
                },
                params: this.getServiceUrlParams()
            });
            //console.log(response.data);
            return response.status === 204;
        } catch (error) {
            if (!handleApiError(error)) {
                console.error('Error fetching documents:', error);
            }
        }

        return false;
    };

    uploadChecklistItemImage = async (sceneObjectUuid: UUID, image: Blob, filename: string): Promise<string | null> => {
        try {
            const formData = new FormData();
            formData.append('image', image, filename);

            const response = await axios.put(
                `${this.configuration.visualizerApiUrl.href}api/v1/private/checklist-items/${sceneObjectUuid}/image`,
                formData,
                {
                    headers: {
                        ...(this.token ? { Authorization: `Bearer ${this.token}` } : {})
                    },
                    params: this.getServiceUrlParams()
                }
            );
            return response.data?.imageUrl ?? null;
        } catch (error) {
            if (!handleApiError(error)) {
                console.error('Error uploading checklist item image:', error);
            }
        }
        return null;
    };

    getCurrentUser = async (gbContext?: IGbContext): Promise<User | null> => {
        try {
            const queryParams = gbContext   ? { ...gbContext, ...this.getServiceUrlParams() } 
                                                : this.getServiceUrlParams();
            const response = await axios.get(`${this.configuration.visualizerApiUrl.href}api/v1/private/users/current`, {
                headers: {
                    'Content-Type': 'application/json',
                    ...(this.token ? { Authorization: `Bearer ${this.token}` } : {})
                },
                params: queryParams
            });
            
            const parsed = getCurrentUserSchema.parse(response.data);

            return new User(parsed.auth0Id);
        } catch (error) {
            console.error('Error fetching current user:', error);
            handleApiError(error);
        }
        return null;
    };
}